// src/sentry.config.ts
import * as Sentry from "@sentry/angular";
import { environment } from "src/environments/environment";
import { VERSION } from "src/environments/version";
import {
    getNavigatorUserAgent,
    getRequestUserAgent,
    isChromeIOSUserAgent,
    isChromiumIOSInjectedScriptStackOverflow,
    isIOSOsName,
} from "./chromium-ios-injected-overflow";
import {
    APP_HOST,
    isUnhandledRejectionWrappedMinifiedError,
    isVettedSalesRouteChunk,
    shouldDropFacebookIOSBridgeError,
} from "./sentry-filters";

/**
 * Returns the client User-Agent for platform checks in beforeSend.
 * event.contexts.os/browser are only populated server-side by Sentry, so they are always
 * empty here; use the request header added by the SDK, falling back to navigator.userAgent.
 */
function getEventUserAgent(event: Sentry.Event): string {
    // Header names are case-insensitive; non-string values are ignored.
    const fromHeaders = getRequestUserAgent(event?.request?.headers);
    if (fromHeaders) return fromHeaders;
    return getNavigatorUserAgent();
}

/**
 * True for Chrome Mobile iOS (CriOS UA token, or server-side contexts when no UA is available).
 *
 * The resolved UA is intentionally authoritative: in the browser it is always available
 * (request header or navigator.userAgent), so the contexts fallback is effectively only used
 * outside a browser. Sentry contexts are server-derived from this same UA, so they cannot carry
 * more information at beforeSend time. Pass `userAgent` to reuse a value resolved once per event.
 */
function isChromeMobileIOSEvent(event: Sentry.Event, userAgent: string = getEventUserAgent(event)): boolean {
    if (userAgent) return isChromeIOSUserAgent(userAgent);
    const browserName = String(event?.contexts?.browser?.name ?? '').trim().toLowerCase();
    return isIOSOsName(event?.contexts?.os?.name) && browserName.includes('chrome mobile ios');
}

// Holds the AuthService.errorHandlerLogs reference once Angular DI is ready.
// initSentry() runs before bootstrapApplication so DI is not available at that
// point; main.ts calls setSentryErrorLogger() inside an APP_INITIALIZER to wire
// the real implementation as soon as the injector is up.
// Until then, messages are buffered (bounded) rather than written to the console or dropped,
// and are replayed into the real logger as soon as setSentryErrorLogger() wires it.
const MAX_EARLY_LOGS = 50;
const _earlyLogs: Array<[string, any?]> = [];
let _earlyLogsTruncated = false;
let _errorHandlerLogs: (message: string, error?: any) => void = (message, error) => {
    // Bounded: once the buffer is full, further early messages are dropped entirely (no buffer
    // entry and no breadcrumb), so repeated early errors cannot evict more useful breadcrumbs.
    if (_earlyLogs.length >= MAX_EARLY_LOGS) {
        // Record truncation exactly once so noisy startups are diagnosable without churn.
        if (!_earlyLogsTruncated) {
            _earlyLogsTruncated = true;
            Sentry.addBreadcrumb({
                category: 'sentry.early-log',
                level: 'warning',
                message: `Early log buffer full (${MAX_EARLY_LOGS}); further pre-DI messages dropped`,
            });
        }
        return;
    }
    _earlyLogs.push([message, error]);
    // Non-console fallback: if bootstrap fails before the real logger is wired, the message is
    // still visible as a breadcrumb on any subsequent Sentry event from this page load.
    Sentry.addBreadcrumb({ category: 'sentry.early-log', level: 'info', message });
};

// Breadcrumb for dropped LOTTOSOCIAL-PWA-1NCB events is added at most once per page load,
// so repeated focus-triggered drops cannot evict more useful breadcrumbs.
let _fbIOSBridgeDropBreadcrumbAdded = false;

// Log the Chromium-iOS injected-script suppression only once per page session to avoid
// flooding the error log stream when the recursion repeats.
let _loggedChromiumIOSOverflowSuppression = false;
// Log a classifier failure only once per page session so a consistently throwing
// classifier cannot flood the browser console.
let _loggedChromiumIOSClassifierFailure = false;

// SALES_ROUTE_CHUNK_RE, APP_HOST and isVettedSalesRouteChunk live in ./sentry-filters
// (side-effect-free, unit-tested) and are imported above.
// Pathname-anchored regex for request URL matching (isAffectedSalesRouteUrl).
// These are top-level Angular SPA routes, so the route segment must be the first path component.
// Stricter than SALES_ROUTE_CHUNK_RE: rejects /a/b/free-reg-step4c (nested paths) that would
// pass the chunk regex but are not valid app page URLs.
// No g/y flag: stateless.
const SALES_ROUTE_PATH_RE = /^\/(free-reg-step4[abcdgh]|free-reg-complete-profile|partial-registration|quick-launch)(?:\/|$)/;

/**
 * True when rawUrl's *pathname* is a known affected sales/registration route on APP_HOST.
 *
 * Only the pathname is tested so that a route name appearing solely in a query parameter
 * or hash fragment (e.g. ?returnUrl=/free-reg-step4c) cannot produce a false-positive match.
 *
 * Input pre-filter: only absolute URLs (https?:// or //) and root-relative paths (starting
 * with /) are accepted. Scheme-less inputs like "evil.com/free-reg-step4c" are rejected
 * upfront to prevent `new URL(input, base)` from silently treating them as relative paths
 * and producing an origin-matching resolved URL on APP_HOST.
 *
 * Origin validation: `parsed.origin` must equal `https://${APP_HOST}` exactly, covering
 * hostname, protocol (HTTPS only), and port. Events from other hosts, protocols (e.g. ftp:),
 * or ports are never suppressed.
 *
 * Fails closed on parse errors: if `new URL()` throws, return false.
 *
 * Uses SALES_ROUTE_PATH_RE (anchored at ^/) rather than SALES_ROUTE_CHUNK_RE so that
 * nested paths like /a/b/free-reg-step4c are not accepted — only top-level Angular routes.
 * SALES_ROUTE_PATH_RE has no g or y flag — repeated .test() calls are stateless.
 */
function isAffectedSalesRouteUrl(rawUrl: string): boolean {
    if (!rawUrl) return false;
    // Reject scheme-less absolute-looking inputs (e.g. "evil.com/path") that new URL()
    // with a base would silently resolve as same-origin relative paths.
    const isAbsolute = /^https?:\/\//i.test(rawUrl) || rawUrl.startsWith('//');
    const isRootRelative = rawUrl.startsWith('/');
    if (!isAbsolute && !isRootRelative) return false;
    try {
        const parsed = new URL(rawUrl, `https://${APP_HOST}`);
        if (parsed.origin !== `https://${APP_HOST}`) return false;
        // Test only the pathname with the root-anchored regex so nested paths are rejected.
        return SALES_ROUTE_PATH_RE.test(parsed.pathname);
    } catch {
        // Fail closed — never suppress unknown/unparseable URLs.
        return false;
    }
}


export function setSentryErrorLogger(fn: (message: string, error?: any) => void): void {
    _errorHandlerLogs = fn;
    // Replay anything logged before DI was ready. Each item is isolated so a throwing logger
    // can neither break the APP_INITIALIZER/bootstrap path nor lose the remaining entries.
    const pending = _earlyLogs.splice(0);
    for (const [message, error] of pending) {
        try {
            fn(message, error);
        } catch {
            // Already recorded as a breadcrumb when first logged; nothing else to do.
        }
    }
}

export function initSentry(): void {
    if (!environment.production) return;

    Sentry.init({
        dsn: "https://5c1ade52ab407e666d04b58371e0874c@o4506259988414464.ingest.us.sentry.io/4506693992841216",
        release: VERSION,
        tracePropagationTargets: [],
        replaysSessionSampleRate: 0.01,
        replaysOnErrorSampleRate: 0.01,
        beforeSend(event, hint) {
            const isLocalhost = window.location.hostname === "localhost";
            if (isLocalhost) return null;

            // Filter out stack overflows thrown inside scripts injected by Chrome Mobile iOS /
            // the Google iOS app (LOTTOSOCIAL-PWA-1NCA, 1NAR, 1NAV, 1NC5, 1NC6, 1NC7, 1NCS, ...).
            // WebKit attributes those frames to the page route (e.g. /account/helpSupport) and the
            // minified functions (e.g. Hk <-> Jk) recurse until the stack overflows. Not app code.
            // Classification is isolated from best-effort logging: once classified as suppressible,
            // the event is dropped even if logging throws.
            let isChromiumIOSInjectedOverflow = false;
            try {
                isChromiumIOSInjectedOverflow = isChromiumIOSInjectedScriptStackOverflow(
                    event as any,
                    window.location.origin,
                    APP_HOST,
                    getNavigatorUserAgent(),
                );
            } catch (classifierError) {
                // Classifier failure must never block reporting; fall through to other filters.
                // Low-noise and console-only (never Sentry) so classifier regressions stay visible.
                if (!_loggedChromiumIOSClassifierFailure) {
                    _loggedChromiumIOSClassifierFailure = true;
                    try {
                        console?.warn?.('[Sentry] Chromium-iOS overflow classifier failed', classifierError);
                    } catch {
                        // Ignore: logging must never break beforeSend.
                    }
                }
            }
            if (isChromiumIOSInjectedOverflow) {
                // console.debug only: never route through _errorHandlerLogs here, since it may
                // report to Sentry and re-enter beforeSend.
                if (!_loggedChromiumIOSOverflowSuppression) {
                    _loggedChromiumIOSOverflowSuppression = true;
                    try {
                        console?.debug?.('[Sentry] Suppressing Chromium-iOS injected-script stack overflow');
                    } catch {
                        // Ignore logging failures.
                    }
                }
                return null;
            }

            // Filter out CleverTap warnings about skipping tracking
            if (event?.message && event?.message?.startsWith('[CleverTap][WARN] Skipping tracking on dev/SW path')) {
                return null; // Ignore this specific warning
            }

            // Filter out CleverTap ReferenceError from TikTok browser incompatibility
            if (event.exception && Array.isArray(event.exception.values) && event.exception.values.length > 0) {
                const firstException = event.exception.values[0];
                const errorMessage = firstException.value || '';
                // Resolve the client UA once per event and reuse it in every platform check.
                const eventUserAgent = getEventUserAgent(event);
                const errorType = firstException.type || '';
                
                // Check if this is a CleverTap error — matches both the standalone
                // clevertap.min.js and the webpack-bundled chunk (node_modules_clevertap-web-sdk_dist_*)
                const isCleverTapError = event.exception.values.some(ex => {
                    const stackFrames = ex.stacktrace?.frames || [];
                    return stackFrames.some(frame => 
                        frame.filename && (
                            frame.filename.includes('clevertap.min.js') ||
                            frame.filename.includes('clevertap-web-sdk')
                        )
                    );
                });

                // Filter out CleverTap ReferenceError (closure issues in non-standard browsers)
                if (errorType === 'ReferenceError' && isCleverTapError) {
                    console.warn('[Sentry] Suppressing CleverTap ReferenceError from non-standard browser (TikTok)');
                    return null;
                }

                // Filter out other known CleverTap errors, including the innerHTML TypeError
                // (LOTTOSOCIAL-PWA-1MY4) caused by isExistingCampaign accessing .innerHTML on
                // a null iframe document. Note: modern Chrome uses "Cannot read properties of"
                // (plural) while older engines used "Cannot read property" (singular).
                // Firefox uses "can't access property" for the same null-access error.
                if (isCleverTapError && (
                    errorMessage.includes("Can't find variable") ||
                    errorMessage.includes("is not defined") ||
                    errorMessage.includes("Cannot read property") ||
                    errorMessage.includes("Cannot read properties of") ||
                    errorMessage.includes("can't access property")
                )) {
                    return null;
                }

                // Existing filter for @smithy/fetch-http-handler
                if (errorMessage.includes("@smithy/fetch-http-handler")) {
                    return null;
                }

                // Filter out minified Checkout.com Web Components SDK init errors (LOTTOSOCIAL-PWA-1N6H).
                // The SDK (checkout-web-components.checkout.com/index.js) is fully minified; internal
                // errors thrown during the first mount attempt surface with mangled 1–4 char messages
                // such as "Aa". These are retried automatically by checkout-flow.service.ts and are not
                // actionable. We match on: short alphabetic-only message + stack from the SDK origin.
                const isCheckoutWebComponentsSdkError = (() => {
                    if (!/^[A-Za-z]{1,4}$/.test(errorMessage)) return false;
                    return event.exception?.values?.some(ex =>
                        (ex.stacktrace?.frames ?? []).some(frame =>
                            frame.filename?.includes('checkout-web-components.checkout.com') ||
                            frame.filename?.includes('checkout.com')
                        )
                    ) ?? false;
                })();
                if (isCheckoutWebComponentsSdkError) {
                    console.debug('[Sentry] Suppressing minified Checkout.com Web Components SDK init error (LOTTOSOCIAL-PWA-1N6H):', errorMessage);
                    return null;
                }

                // Filter out short minified errors thrown by content-blocker GA stubs or GTM-injected
                // analytics scripts (LOTTOSOCIAL-PWA-1N7V).
                // When a browser content blocker (e.g. Brave, uBlock Origin, iOS Safari content blocker)
                // intercepts window.ga it replaces it with a stub that throws new Error('ga'). The app has
                // no guard around the ga() call so the stub's error surfaces as an unhandled Error with the
                // minified message "ga" (or similar 1–4 char alphabetic string). The stack resolves only to
                // https://app.lottosocial.com/ (the root HTML document) with no JS bundle origin,
                // making it unactionable noise. We require at least one frame to be present and all frames
                // to come exclusively from the root HTML document (inline script source) to avoid suppressing
                // legitimate app errors that happen to have no stack or short messages.
                const rootDocumentOrigin = window.location.origin + '/';
                const isGaContentBlockerError = (() => {
                    if (errorType !== 'Error') return false;
                    if (!/^[A-Za-z]{1,4}$/.test(errorMessage)) return false;
                    const allFrames = (event.exception?.values ?? []).flatMap(ex => ex.stacktrace?.frames ?? []);
                    // Require at least one frame so we do not match zero-stack errors broadly
                    if (allFrames.length === 0) return false;
                    // All frames must point to the root HTML document (inline script), not a JS bundle
                    return allFrames.every(frame =>
                        !frame.filename || frame.filename === rootDocumentOrigin
                    );
                })();
                if (isGaContentBlockerError) {
                    console.debug('[Sentry] Suppressing content-blocker GA stub error (LOTTOSOCIAL-PWA-1N7V):', errorMessage);
                    return null;
                }

                // Filter out cross-origin iframe SecurityError (expected when accessing payment frames)
                // The Checkout.com Frames library (framesv2.min.js) attempts to interact with payment iframes
                // that are hosted on different origins. iOS Safari enforces stricter cross-origin restrictions.
                // This error is expected and handled gracefully by the application.
                if (errorType === 'SecurityError' && (
                    errorMessage.includes('cross-origin') || 
                    errorMessage.includes('Blocked a frame')
                )) {
                    console.debug('[Sentry] Suppressing expected cross-origin iframe SecurityError from Checkout.com Frames library');
                    return null;
                }

                // Filter out the Android Chrome variant of the same cross-origin iframe bug.
                // On Android Chrome, cross-origin iframe contentWindow returns null instead of throwing
                // a SecurityError (the iOS Safari behaviour above). framesv2.min.js then accesses
                // .document on that null value, producing this TypeError. The contentWindowNullGuard
                // polyfill prevents the crash at runtime; this filter removes any residual reports
                // that slip through before the polyfill is installed (e.g. during script evaluation).
                if (errorType === 'TypeError' &&
                    errorMessage.includes("Cannot read properties of null (reading 'document')")
                ) {
                    const isFramesv2Error = event.exception?.values?.some(ex => {
                        const frames = ex.stacktrace?.frames ?? [];
                        return frames.some(frame =>
                            frame.filename?.includes('framesv2') ||
                            frame.filename?.includes('cdn.checkout.com')
                        );
                    });
                    if (isFramesv2Error) {
                        console.debug('[Sentry] Suppressing expected framesv2 contentWindow.document TypeError (Android Chrome)');
                        return null;
                    }
                }

                // Filter out Cloudflare Turnstile JSON parse errors (third-party bug)
                // Turnstile attempts JSON.parse(JSON.stringify(undefined)) which throws SyntaxError
                const isTurnstileError = event.exception.values.some(ex => {
                    const stackFrames = ex.stacktrace?.frames || [];
                    return stackFrames.some(frame => 
                        frame.filename && (
                            frame.filename.includes('/turnstile/v0/api.js') ||
                            frame.filename.includes('challenges.cloudflare.com')
                        )
                    );
                });

                if (errorType === 'SyntaxError' && isTurnstileError && (
                    errorMessage.includes('"undefined" is not valid JSON') ||
                    errorMessage.includes('is not valid JSON') ||
                    errorMessage.includes('Unexpected end of input') ||
                    errorMessage.includes('Non-number found after exponent indicator')
                )) {
                    console.warn('[Sentry] Suppressing Cloudflare Turnstile JSON parse error (known third-party issue)');
                    return null;
                }

                // Filter out Facebook In-App Browser postMessage error (iabjs://navigation_performance_logger_android)
                // Facebook's IAB injects a performance logger that calls sendDataToNative/postMessage during WebView
                // teardown. By the time it fires, the native Java bridge is already GC'd — this is a Facebook IAB
                // bug, not ours. The error is unhandled but does not affect the user experience.
                const isFacebookIABError = event.exception.values.some(ex => {
                    const stackFrames = ex.stacktrace?.frames || [];
                    return stackFrames.some(frame =>
                        frame.filename && (
                            frame.filename.includes('iabjs://') ||
                            frame.filename.includes('navigation_performance_logger_android')
                        )
                    );
                });

                if (isFacebookIABError) {
                    console.debug('[Sentry] Suppressing Facebook IAB postMessage error (Java object is gone)');
                    return null;
                }

                // Filter out Instagram iOS IAB window.webkit.messageHandlers error
                // Instagram's in-app browser on iOS injects a script that calls
                // window.webkit.messageHandlers.sendDataToNative — an API only available
                // inside native WKWebView shells. Instagram's IAB doesn't expose this object,
                // causing an unhandled TypeError. This is a third-party IAB bug, not ours.
                const isInstagramIOSIABError = event.exception.values.some(ex =>
                    ex.type === 'TypeError' &&
                    ex.value?.includes('window.webkit.messageHandlers')
                );

                if (isInstagramIOSIABError) {
                    console.debug('[Sentry] Suppressing Instagram iOS IAB window.webkit.messageHandlers error');
                    return null;
                }

                // Filter out Facebook iOS IAB native bridge InvalidAccessError (LOTTOSOCIAL-PWA-1NCB)
                // Facebook's iOS in-app browser injects a login-form tracking script
                // (logLoginFormFocused -> sendPostMessage -> dispatchToBridge) that calls the native
                // WKWebView postMessage when a form input is focused. The native bridge rejects with
                // InvalidAccessError (DOMException code 15) and Meta's script never handles it.
                // These functions don't exist in our codebase — this is a third-party IAB bug.
                // Gated on: Facebook iOS IAB user agent, InvalidAccessError (as type or DOMException
                // value), and the full logLoginFormFocused -> sendPostMessage -> dispatchToBridge
                // chain as adjacent frames, each attributed to a vetted sales-route path on our origin.
                // Decision logic lives in shouldDropFacebookIOSBridgeError (unit-tested end to end;
                // accepts undefined/empty exceptions and never throws).
                let dropFbIOSBridgeError = false;
                try {
                    dropFbIOSBridgeError = shouldDropFacebookIOSBridgeError(event.exception?.values);
                } catch {
                    // Fail open: never lose an event because the filter itself misbehaved.
                    dropFbIOSBridgeError = false;
                }
                if (dropFbIOSBridgeError) {
                    // No console output in production. Leave a low-noise breadcrumb (once per
                    // page load) so a false positive can still be diagnosed from a later event.
                    if (!_fbIOSBridgeDropBreadcrumbAdded) {
                        _fbIOSBridgeDropBreadcrumbAdded = true;
                        Sentry.addBreadcrumb({
                            category: 'sentry.filter',
                            level: 'info',
                            message: 'Dropped Facebook iOS IAB bridge InvalidAccessError (LOTTOSOCIAL-PWA-1NCB)',
                        });
                    }
                    return null;
                }

                // [LOTTOSOCIAL-PWA-1NCT] Drop wrapped minified internal errors of the form
                // "Error: XY" (XY = 1–3 ASCII letters, e.g. "Error: La") that arrive via
                // onunhandledrejection. These are Angular/Firebase internals triggered by
                // Firestore network drops on iOS/WebKit and carry no diagnostic value.
                // Both the mechanism and the value pattern are checked on the PRIMARY exception
                // (the last entry in exception.values) so a chained event where one exception
                // has the mechanism and a different one has the short value is never dropped.
                let dropWrappedMinifiedError = false;
                try {
                    dropWrappedMinifiedError = isUnhandledRejectionWrappedMinifiedError(
                        event.exception?.values
                    );
                } catch {
                    dropWrappedMinifiedError = false;
                }
                if (dropWrappedMinifiedError) {
                    return null;
                }

                // Filter out Chatlio widget JSON parse errors (third-party widget bug)
                // The Chatlio widget sometimes receives non-JSON responses from api.chatlio.com
                // Optional access for consistency; this block is already guarded by the
                // non-empty event.exception.values check at the top of beforeSend.
                const isChatlioError = (event.exception?.values ?? []).some(ex => {
                    const stackFrames = ex.stacktrace?.frames || [];
                    return stackFrames.some(frame => 
                        frame.filename && (
                            frame.filename.includes('chatlio-widget.js') ||
                            frame.filename.includes('w.chatlio-widget.js')
                        )
                    );
                });

                if (errorType === 'SyntaxError' && isChatlioError && (
                    errorMessage.includes('is not valid JSON') ||
                    errorMessage.includes('"token_expir"') ||
                    errorMessage.includes('Unexpected token')
                )) {
                    console.warn('[Sentry] Suppressing Chatlio widget JSON parse error (third-party widget issue)');
                    return null;
                }

                // Filter out Cloudflare beacon.min.js XHRInterface errors (race condition with Zone.js)
                // This is a known issue where beacon.min.js attempts to use XHR while Zone.js is patching XMLHttpRequest
                const isBeaconError = event.exception.values.some(ex => {
                    const stackFrames = ex.stacktrace?.frames || [];
                    return stackFrames.some(frame => 
                        frame.filename && (
                            frame.filename.includes('beacon.min.js') ||
                            frame.filename.includes('static.cloudflareinsights.com')
                        )
                    );
                });

                if (errorType === 'ReferenceError' && isBeaconError && (
                    errorMessage.includes('XHRInterface is not defined') ||
                    errorMessage.includes('XHRInterface')
                )) {
                    console.warn('[Sentry] Suppressing Cloudflare beacon XHRInterface error (Zone.js race condition)');
                    return null;
                }

                // Filter out Cloudflare beacon.min.js Array.prototype.at() compatibility errors
                // beacon.min.js uses entries.at(-1) (ES2022) which is unsupported in older browsers
                // such as Oculus Browser 10.16.0. This is a third-party script we cannot modify
                // and the error does not affect app functionality. (LOTTOSOCIAL-PWA-1N79)
                if (errorType === 'TypeError' && isBeaconError && errorMessage.includes('.at is not a function')) {
                    console.warn('[Sentry] Suppressing Cloudflare beacon .at() compatibility error (legacy browser)');
                    return null;
                }

                // Filter out OneSignal SDK "No subscription" errors (expected on iOS Safari / unsubscribed users)
                // OneSignal's SessionManager throws this when it attempts to upsert a session but the user
                // has no active push subscription. This is unactionable and does not affect app functionality.
                const isOneSignalSessionError = event.exception.values.some(ex => {
                    const stackFrames = ex.stacktrace?.frames || [];
                    return stackFrames.some(frame =>
                        frame.filename && frame.filename.includes('sessionManager/SessionManager')
                    );
                });

                if (errorMessage === 'No subscription' && isOneSignalSessionError) {
                    return null;
                }

                // Filter out short minified Angular/Firebase internal errors surfacing as unhandled
                // promise rejections or window.onerror throws on iOS WebKit (Chrome Mobile iOS) during
                // partial-registration and the 4C payment page
                // (LOTTOSOCIAL-PWA-1NA1, LOTTOSOCIAL-PWA-1NAB, LOTTOSOCIAL-PWA-1NBR).
                // Examples: "Pa", "Ba", "fa", "La", "pa".
                // These bypass GlobalErrorHandler's equivalent ≤3-char filter because the Sentry SDK's
                // listener fires before Angular's error pipeline. Common preconditions (applied to
                // ALL exception values via every(), not just the first):
                //   1. Every value must have type === 'Error' and a 1–3 alphabetic-char message.
                //      This prevents a multi-value event where one value has a short minified message
                //      and another has a real error message from being incorrectly suppressed.
                //   2. All values must share the same mechanism (mixed-mechanism events are kept).
                // Then two mechanism-specific paths:
                //   - onunhandledrejection: named frames pooled across all values must all be known
                //     app-bundle frames (hashed bundles, Firebase paths, or vetted sales-route chunk
                //     paths — SALES_ROUTE_CHUNK_RE).
                //   - onerror (LOTTOSOCIAL-PWA-1NBR): also gated on iOS + Chrome Mobile iOS and the
                //     request URL being on a known affected route (SALES_ROUTE_PATH_RE). Every value
                //     must independently pass isBundleFrame and have ≥1 frame matching SALES_ROUTE_CHUNK_RE.
                const isMinifiedAngularInternalError = (() => {
                    if (errorType !== 'Error') return false;
                    if (!/^[A-Za-z]{1,3}$/.test(errorMessage)) return false;

                    const isBundleFrame = (filename: string): boolean => {
                        if (!filename) return false;
                        // For absolute URLs, require the frame to originate from APP_HOST (HTTPS only)
                        // or a known Firebase hostname before applying bundle-pattern matching. Without
                        // this, a third-party frame URL containing '/main.' or '/chunk-' could pass.
                        // Protocol-relative URLs (//host/path) are normalized to https: before parsing
                        // because new URL('//host/path') throws without a base.
                        const looksAbsolute = /^https?:\/\//i.test(filename) || filename.startsWith('//');
                        if (looksAbsolute) {
                            try {
                                const normalized = filename.startsWith('//') ? `https:${filename}` : filename;
                                const parsed = new URL(normalized);
                                const isAppOrigin = parsed.origin === `https://${APP_HOST}`;
                                const isFirebaseHost = parsed.hostname.endsWith('.firebaseapp.com') ||
                                    parsed.hostname.endsWith('.firebase.google.com') ||
                                    parsed.hostname.endsWith('.firebasestorage.googleapis.com');
                                if (!isAppOrigin && !isFirebaseHost) return false;
                            } catch {
                                return false;
                            }
                        }
                        return (
                            filename.includes('/main.') ||
                            filename.includes('/polyfills.') ||
                            filename.includes('/vendor.') ||
                            filename.includes('/chunk-') ||
                            // Bare/relative sales-route paths, or absolute URLs whose hostname is APP_HOST.
                            isVettedSalesRouteChunk(filename) ||
                            (
                                (filename.includes('/firebase') || filename.includes('firebase.') || filename.includes('firebase-')) &&
                                !filename.includes('gstatic.com') &&
                                !filename.includes('cdn.')
                            )
                        );
                    };

                    const exceptionValues = event.exception?.values ?? [];
                    if (exceptionValues.length === 0) return false;

                    // Validate every exception value's type and message — not just the first.
                    // The outer errorType/errorMessage checks only cover the first value; a multi-value
                    // event where one value has a short minified message and another has a real error
                    // message would otherwise pass the outer check and be incorrectly suppressed.
                    if (!exceptionValues.every(ex =>
                        ex.type === 'Error' && /^[A-Za-z]{1,3}$/.test(ex.value ?? '')
                    )) return false;

                    // Reject mixed-mechanism events upfront: all values must share the same mechanism.
                    // This prevents a single qualifying value from masking a real error with a different
                    // mechanism, and makes each path's frame evaluation consistent.
                    const sharedMechanism = exceptionValues[0]?.mechanism?.type;
                    if (!sharedMechanism) return false;
                    if (!exceptionValues.every(ex => ex.mechanism?.type === sharedMechanism)) return false;

                    if (sharedMechanism === 'onunhandledrejection') {
                        // Pool named frames across all values (all share the same mechanism).
                        const namedFrames = exceptionValues
                            .flatMap(ex => ex.stacktrace?.frames ?? [])
                            .filter(frame => !!frame.filename);
                        if (namedFrames.length === 0) return false;
                        return namedFrames.every(frame => isBundleFrame(frame.filename!));
                    }

                    if (sharedMechanism === 'onerror') {
                        // Platform guard: every observed occurrence is on iOS + Chrome Mobile iOS.
                        // Scoping to the specific observed browser (not all WebKit browsers) avoids
                        // broadening suppression to Mobile Safari or other iOS browsers that have not
                        // exhibited this pattern.
                        if (!isChromeMobileIOSEvent(event, eventUserAgent)) return false;

                        // URL guard: delegate to isAffectedSalesRouteUrl which parses the URL,
                        // validates origin === https://APP_HOST, and tests only the pathname
                        // (never query string or hash). Fails closed on malformed input. See JSDoc.
                        if (!isAffectedSalesRouteUrl(event.request?.url ?? '')) return false;

                        // Every value must independently qualify so no single value can mask a real error.
                        return exceptionValues.every(ex => {
                            const namedFrames = (ex.stacktrace?.frames ?? []).filter(frame => !!frame.filename);
                            if (namedFrames.length === 0) return false;
                            if (!namedFrames.every(frame => isBundleFrame(frame.filename!))) return false;
                            // Require at least one vetted sales-route chunk frame to pin suppression to
                            // the specific registration/payment pages (free-reg-step4c etc.).
                            return namedFrames.some(frame => isVettedSalesRouteChunk(frame.filename!));
                        });
                    }

                    return false;
                })();
                if (isMinifiedAngularInternalError) {
                    console.debug('[Sentry] Suppressing minified Angular/Firebase internal error (LOTTOSOCIAL-PWA-1NA1, LOTTOSOCIAL-PWA-1NAB, LOTTOSOCIAL-PWA-1NBR):', errorMessage);
                    return null;
                }

                // Filter out chatbot.com widget load failures (transient network issue, not an invalid ID)
                // The chatbot.com SDK throws an unhandled promise rejection with the value
                // "[chatbot.com] - Widget with this ID does not exist" whenever it cannot fetch the
                // widget config (e.g. the user is offline or the request fails). The widget ID is valid
                // and loads correctly once connectivity is restored, so this is unactionable noise.
                if (errorMessage.includes('[chatbot.com]') && errorMessage.includes('Widget with this ID does not exist')) {
                    return null;
                }

                // Filter out Firebase IndexedDB corruption errors (expected / self-recovering condition)
                // Firebase intentionally throws this when its IndexedDB version cache mismatches the
                // actual DB version after the user (or Android Chrome) clears site data.
                // The IndexedDBRecoveryService handles this by clearing the DB and reloading the page.
                if (errorMessage.includes('refusing to open IndexedDB') ||
                    errorMessage.includes('potential corruption of the IndexedDB')) {
                    console.warn('[Sentry] Suppressing Firebase IndexedDB corruption error — recovery in progress');
                    return null;
                }

                // Filter out CleverTap findTopmostVisibleElement infinite recursion (LOTTOSOCIAL-PWA-1M1F)
                // and minified Angular CD re-entry stack overflows on iOS (LOTTOSOCIAL-PWA-1NC5).
                //
                // Two manifestations of the same class of bug — both are RangeError/Maximum call stack
                // size exceeded triggered by synchronous mutual recursion in minified Angular/SDK code
                // bundled into lazy-loaded chunks on iOS + Chrome Mobile iOS:
                //
                // 1. LOTTOSOCIAL-PWA-1M1F: CleverTap SDK's findTopmostVisibleElement DOM traversal.
                //    The SDK is bundled into Angular lazy chunks so the clevertap.min.js filename
                //    check misses it; match by function name instead.
                //
                // 2. LOTTOSOCIAL-PWA-1NC5: Angular change-detection re-entry caused by synchronous
                //    component-state mutations inside map() operators in the games page, cascading
                //    into the PaymentPage (free-reg-step4c) chunk. In the minified build the
                //    offending functions are short identifiers (e.g. Lk, Jk) with no readable name.
                //    Match by: iOS + Chrome Mobile iOS + all stack frames in known app route chunks
                //    + no frame anywhere in the event with a readable (non-minified) function name.
                if (errorType === 'RangeError' &&
                    errorMessage.includes('Maximum call stack size exceeded')
                ) {
                    // Compute once; used in both cases below.
                    const rangeErrorValues = event.exception?.values ?? [];

                    // Case 1: CleverTap findTopmostVisibleElement — iOS-only SDK bug, match by
                    // readable function name. The bug only manifests on iOS (LOTTOSOCIAL-PWA-1M1F),
                    // so gate on iOS OS name or an iOS/iPadOS User-Agent before suppressing.
                    // Prefer the already-resolved eventUserAgent (request header / navigator) because
                    // contexts.os is empty at beforeSend time in the browser SDK.
                    const isIosEvent =
                        isIOSOsName(event?.contexts?.os?.name) ||
                        /\b(iPhone|iPad|iPod)\b/i.test(eventUserAgent) ||
                        (/\bMacintosh\b/i.test(eventUserAgent) && /\bMobile\//i.test(eventUserAgent));
                    const hasCleverTapTopmostVisibleFrame = isIosEvent && rangeErrorValues.some(ex =>
                        (ex.stacktrace?.frames ?? []).some(frame =>
                            frame.function && frame.function.includes('findTopmostVisibleElement')
                        )
                    );
                    if (hasCleverTapTopmostVisibleFrame) {
                        _errorHandlerLogs('[Sentry] Suppressing CleverTap findTopmostVisibleElement infinite recursion (iOS bug — LOTTOSOCIAL-PWA-1M1F)');
                        return null;
                    }

                    // Case 2: Fully minified mutual-recursion on iOS Chrome Mobile in known route
                    // chunks (LOTTOSOCIAL-PWA-1NC5). Only suppress when ALL of these hold:
                    //   a) Device is iOS + Chrome Mobile iOS
                    //   b) The matching exception value's mechanism is "onerror" (uncaught error)
                    //   c) At least 8 frames in that exception's stack
                    //   d) Every named frame in that stack is a known route chunk or framework chunk
                    //   e) No readable user-land function name longer than 4 chars
                    const isIosChromeMobile = isChromeMobileIOSEvent(event, eventUserAgent);
                    if (isIosChromeMobile) {
                        // The observed LOTTOSOCIAL-PWA-1NC5 stack uniquely spans TWO specific
                        // Angular lazy-loaded chunks:
                        //   • A mainTabs/tabs/* route chunk (e.g. mainTabs/tabs/game)
                        //   • A sales/payment route chunk  (e.g. free-reg-step4c)
                        // Requiring both to be present in the SAME exception stack is a much
                        // tighter fingerprint than matching any single route chunk.
                        // Bundle separators (`.`, `-`, `_`, `/`) are allowed after the segment
                        // so hashed filenames like "mainTabs/tabs/game.abc123.js" still match.
                        const tabsChunkRe = /(^|\/)mainTabs\/tabs\/[^/?#.][^/?#]*(?:[./_\-?#]|$)/;
                        const salesChunkRe = /(^|\/)(free-reg-step4[a-z]+|free-reg-complete-profile|partial-registration|quick-launch)(?:[./_\-?#]|$)/;
                        // Framework/runtime chunks (polyfills, vendor, main, zone, runtime)
                        // may appear in a truncated stack. Anchored to a path segment boundary
                        // so substrings like "main" in "domain.js" are not falsely matched.
                        const frameworkChunkRe = /(^|\/)(?:polyfills|vendor|runtime|main|zone)(?:[.\-][^/]*)?\.js(?:[?#]|$)/;

                        // Find the first exception value that:
                        //   1. Has mechanism "onerror" (uncaught error)
                        //   2. Has frames from BOTH observed chunk types in its own stack
                        // Using the first MATCHING exception — not always index 0 — handles
                        // chained exceptions where Sentry may reorder values.
                        const matchingEx = rangeErrorValues.find(ex => {
                            if ((ex.mechanism?.type ?? '') !== 'onerror') return false;
                            const frames = ex.stacktrace?.frames ?? [];
                            const named = frames.filter(f => !!f.filename);
                            return named.some(f => tabsChunkRe.test(f.filename!)) &&
                                named.some(f => salesChunkRe.test(f.filename!));
                        });
                        if (matchingEx) {
                            const allFrames = matchingEx.stacktrace?.frames ?? [];
                            // Require a minimum frame count — mutual recursion until stack overflow
                            // always produces many frames; a shallow stack is not this bug.
                            const hasEnoughFrames = allFrames.length >= 8;
                            // Every named frame must be from a known route chunk or an expected
                            // framework/runtime chunk — anything else is unexpected and should not
                            // be suppressed.
                            const namedFrames = allFrames.filter(f => !!f.filename);
                            const allInKnownChunks = namedFrames.length > 0 &&
                                namedFrames.every(f =>
                                    tabsChunkRe.test(f.filename!) ||
                                    salesChunkRe.test(f.filename!) ||
                                    frameworkChunkRe.test(f.filename!)
                                );
                            // No frame may have a readable user-land function name. Minified
                            // identifiers are 1-4 chars; anything longer is potentially readable.
                            // Exclude known framework/runtime/browser-generated name prefixes
                            // that are longer than 4 chars but are not user-land code.
                            // Note: generic Object.*/Array.*/Promise.* are intentionally NOT
                            // excluded — user-land minifiers can produce "Object.handlePaymentError"
                            // and similar, which must not be suppressed.
                            const FRAMEWORK_FN_RE = /^(?:<|__zone_symbol__|__webpack|ZoneDelegate|Zone(?:Task|AwarePromise)?|dispatchEvent|invokeTask|checkStable|drainMicroTaskQueue)/i;
                            const hasReadableFunction = allFrames.some(f => {
                                if (!f.function || f.function.length <= 4) return false;
                                return !FRAMEWORK_FN_RE.test(f.function);
                            });
                            if (hasEnoughFrames && allInKnownChunks && !hasReadableFunction) {
                                _errorHandlerLogs('[Sentry] Suppressing minified Angular CD re-entry stack overflow on iOS Chrome Mobile (LOTTOSOCIAL-PWA-1NC5)');
                                return null;
                            }
                        }
                    } // end if (isIosChromeMobile)
                } // end if (errorType === 'RangeError' && Maximum call stack)

                // Filter out stack overflows inside Chromium-on-iOS browser-injected scripts
                // (LOTTOSOCIAL-PWA-1NCA, 1NAR, 1NAV, 1NC5, 1NC6, 1NC7).
                // Chrome Mobile iOS / the Google iOS app inject their own minified scripts at
                // document start (e.g. autofill / DOM traversal). WebKit attributes those frames to
                // the page URL at injection time (e.g. /quick-launch, /mainTabs/tabs/home) rather
                // than to a .js bundle. Their internal mutual recursion (Jk <-> Lk) overflows the
                // stack on deep Ionic/shadow-DOM trees. This is not app code and cannot be fixed here.
                // We only suppress when EVERY frame is a same-origin page-route path (no script file)
                // and the frames match the exact alternating minified-recursion signature, so any
                // stack overflow touching a real app bundle frame is still reported.
                // Logic lives in chromium-ios-injected-overflow.ts (pure + unit-tested).
                // APP_HOST is a bare hostname constant; the helper also accepts an absolute URL.
                // The classifier is wrapped so an unexpected event shape can never break
                // beforeSend — on failure we fall through and report the event normally.
                let isInjectedOverflow = false;
                try {
                    const documentOrigin = typeof window !== 'undefined' ? (window.location?.origin ?? '') : '';
                    isInjectedOverflow = isChromiumIOSInjectedScriptStackOverflow(
                        event,
                        documentOrigin,
                        APP_HOST,
                        eventUserAgent,
                    );
                } catch {
                    isInjectedOverflow = false;
                }
                if (isInjectedOverflow) {
                    if (!_loggedChromiumIOSOverflowSuppression) {
                        _loggedChromiumIOSOverflowSuppression = true;
                        _errorHandlerLogs('[Sentry] Suppressing Chromium-on-iOS injected script stack overflow (LOTTOSOCIAL-PWA-1NCA)');
                    }
                    return null;
                }

                // Filter out Firestore permission-denied errors from auth/token race conditions
                // (LOTTOSOCIAL-PWA-1MEE, 1MCA, 1MED, 1M67, 1MGP). These surface as unhandled
                // FirebaseError from Firestore onSnapshot before the custom token is applied —
                // not real access-control denials. Security rules still deny unauthorized access;
                // we only stop reporting the unhandled SDK race as an app error.
                //
                // Match: permission message + (FirebaseError | Error wrapper) + stack only in
                // minified bundles (vendor/polyfills/main/chunk). Real app bugs that include
                // first-party source frames are still reported.
                const isFirestoreAuthRacePermissionDenied = (() => {
                    const isPermissionMsg =
                        errorMessage.includes('Missing or insufficient permissions') ||
                        errorMessage.includes('permission-denied') ||
                        errorMessage.includes('[code=permission-denied]');
                    if (!isPermissionMsg) return false;

                    const isFirebasePermissionType =
                        errorType === 'FirebaseError' ||
                        errorType === 'Error' ||
                        errorType === 'UnhandledRejection';
                    if (!isFirebasePermissionType) return false;

                    const allFrames = (event.exception?.values ?? []).flatMap(
                        ex => ex.stacktrace?.frames ?? []
                    );
                    if (allFrames.length === 0) {
                        // No stack (common on some mobile browsers) — still drop when message
                        // matches; these are never actionable without frames.
                        return true;
                    }

                    const isBundleFrame = (filename?: string) =>
                        !!filename && (
                            filename.includes('/vendor.') ||
                            filename.includes('/polyfills.') ||
                            filename.includes('/main.') ||
                            filename.includes('/chunk-') ||
                            filename.includes('firebase') ||
                            filename === '<anonymous>'
                        );

                    return allFrames.every(frame => !frame.filename || isBundleFrame(frame.filename));
                })();
                if (isFirestoreAuthRacePermissionDenied) {
                    console.debug('[Sentry] Suppressing Firestore auth-race permission-denied (LOTTOSOCIAL-PWA-1MEE)');
                    return null;
                }
            }

            const isNonErrorException =
              (event?.exception?.values?.[0]?.value?.startsWith("Non-Error exception captured") ?? false) ||
              (hint?.originalException &&
                typeof hint?.originalException?.["message"] === "string" &&
                hint?.originalException?.["message"]?.startsWith("Non-Error exception captured"));

            if (isNonErrorException) return null;

            return event;
        },
    });

    Sentry.setTag("app_version", VERSION);
}
